import { formatChangePath, isMembershipChange, isRedacted } from '../utils/auditChanges';

const isEmptyValue = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' && Object.keys(value).length === 0);

/** Flatten a value to text, withholding anything the backend redacted. */
export const extractValue = (value, indent = 0) => {
  if (isEmptyValue(value)) return '—';
  if (isRedacted(value)) return 'withheld';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return (
      value
        .map((item) => extractValue(item, indent))
        .filter((item) => item !== '—')
        .join('\n') || '—'
    );
  }

  if (typeof value === 'object') {
    const pad = '  '.repeat(indent);

    const lines = Object.entries(value)
      .map(([key, item]) => {
        if (isEmptyValue(item)) return null;

        if (typeof item === 'object' && !Array.isArray(item)) {
          const nested = extractValue(item, indent + 1);
          return nested === '—' ? null : `${pad}${key}:\n${nested}`;
        }

        return `${pad}${key}: ${extractValue(item, indent)}`;
      })
      .filter(Boolean);

    return lines.length ? lines.join('\n') : '—';
  }

  return String(value);
};

const LeafValue = ({ value }) =>
  isRedacted(value) ? (
    <span className="redacted-badge">withheld</span>
  ) : (
    <span className="change-value">{extractValue(value)}</span>
  );

/**
 * One change record.
 *
 * The diff reports three shapes: a scalar edit, a membership change for items
 * joining or leaving a list, and a pair of withheld values when a secret moved.
 */
export function AuditChangeValue({ value }) {
  if (isMembershipChange(value)) {
    return (
      <span className="change-membership">
        {(value.added || []).map((item, index) => (
          <span className="change-added" key={`added-${index}`}>
            + {extractValue(item)}
          </span>
        ))}
        {(value.removed || []).map((item, index) => (
          <span className="change-removed" key={`removed-${index}`}>
            − {extractValue(item)}
          </span>
        ))}
      </span>
    );
  }

  // Both sides withheld: the fingerprints must differ or there would be no
  // change record, but "withheld → withheld" reads as unchanged.
  if (isRedacted(value?.old) && isRedacted(value?.new)) {
    return <span className="redacted-badge">secret replaced</span>;
  }

  return (
    <span className="change-scalar">
      <LeafValue value={value?.old} />
      <span className="change-arrow">→</span>
      <LeafValue value={value?.new} />
    </span>
  );
}

export function AuditChangesSection({ changes }) {
  if (!changes || !Object.keys(changes).length) return null;

  return (
    <div className="radius-yes p-2 bg-yes col-12">
      <h2 className="h5-title">Changes</h2>
      <div className="radius-yes bg-yes">
        {Object.entries(changes).map(([field, value]) => (
          <div className="detail-item" key={field}>
            <label>
              <strong>{formatChangePath(field)}:</strong>
            </label>
            <span style={{ whiteSpace: 'pre-line' }}>
              <AuditChangeValue value={value} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** A whole-entity snapshot: what was created, what was deleted, or the state
 *  before an update that left the entity unreadable. */
export function AuditSnapshotSection({ title, snapshot }) {
  if (!snapshot || !Object.keys(snapshot).length) return null;

  return (
    <div className="radius-yes p-2 bg-yes col-12">
      <h2 className="h5-title">{title}</h2>
      <div className="radius-yes bg-yes">
        {Object.entries(snapshot).map(([field, value]) => (
          <div className="detail-item" key={field}>
            <label>
              <strong>{formatChangePath(field)}:</strong>
            </label>
            <span style={{ whiteSpace: 'pre-line' }}>{extractValue(value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
